import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { browserSupportsWebAuthn, startRegistration } from '@simplewebauthn/browser'
import { Button } from 'ui/button'
import { toast } from 'ui/sonner'

import { apiClient } from '~/api/client'
import type { components } from '~/api/types'
import { useAccountStore } from '~/stores/account'

export const PasswordlessRegister = () => {
  const webauthnRegistered = useAccountStore(state => state.webauthnRegistered)
  const patchAccount = useAccountStore(state => state.patchAccount)
  const [isHidden, setIsHidden] = useState(false)
  const { t } = useTranslation()
  if (!browserSupportsWebAuthn()) return null

  if (webauthnRegistered) return null

  if (isHidden) return null

  const handleRegister = async () => {
    try {
      const { data: options } = await apiClient.GET('/webauthn/register')
      if (!options) return

      const credentials = await startRegistration(options)
      const { data: { verified } = {} } = await apiClient.POST('/webauthn/register-complete', {
        body: credentials as components['schemas']['RegistrationResponseJSONDto'],
      })
      if (verified) {
        patchAccount({ webauthnRegistered: true })
        setIsHidden(true)

        return toast.success(t('Authenticator was registered'))
      }

      toast.error(t('Something went wrong'))
    } catch (error) {
      const webauthnWasProbablyRegisteredBefore =
        typeof error === 'object' && error && 'name' in error && error.name === 'InvalidStateError'
      if (webauthnWasProbablyRegisteredBefore) {
        console.error({ message: 'Authenticator was probably already registered' })

        patchAccount({ webauthnRegistered: true })

        return setIsHidden(true)
      }

      console.error({ message: 'Failed to register authenticator', error })
      toast.error((error as Error).message || t('Something went wrong'))
    }
  }

  return (
    <Button onClick={handleRegister} type="button">
      {t('Enable passwordless login')}
    </Button>
  )
}
